import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Radar, 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  ResponsiveContainer,
  Legend
} from 'recharts';
import { 
  ArrowLeft, 
  GitCompare,
  Home,
  Trophy,
  Target,
  Users,
  Crown
} from "lucide-react";

const SKILLS = [
  { key: "ataque", label: "Ataque" },
  { key: "passe", label: "Passe" },
  { key: "defesa", label: "Defesa" },
  { key: "saque", label: "Saque" },
  { key: "bloqueio", label: "Bloqueio" },
  { key: "toque", label: "Toque" },
  { key: "skill", label: "Skill" },
];

const RADAR_TICK = { fill: '#a1a1aa', fontSize: 12 };
const RADAR_RADIUS_TICK = { fill: '#a1a1aa' };
const RADAR_DOMAIN = [0, 10];

export default function TeamsComparePage({ evaluator }) {
  const navigate = useNavigate();
  const [teamsAverages, setTeamsAverages] = useState([]);
  const [team1Id, setTeam1Id] = useState("");
  const [team2Id, setTeam2Id] = useState("");
  const [comparison, setComparison] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/teams/all/averages`);
      setTeamsAverages(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompare = async () => {
    if (!team1Id || !team2Id) {
      toast.error("Selecione dois times para comparar");
      return;
    }
    if (team1Id === team2Id) {
      toast.error("Selecione times diferentes");
      return;
    }

    try {
      const response = await axios.get(`${API}/teams/compare/${team1Id}/${team2Id}`);
      setComparison(response.data);
    } catch (error) {
      toast.error("Erro ao comparar times");
    }
  };

  const getOverallAverage = (avg) => {
    if (!avg?.averages) return 0;
    const values = Object.values(avg.averages);
    return (values.reduce((a, b) => a + b, 0) / values.length).toFixed(1);
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const getWinner = () => {
    if (!comparison) return null;
    const avg1 = parseFloat(getOverallAverage(comparison.team1));
    const avg2 = parseFloat(getOverallAverage(comparison.team2));
    if (avg1 > avg2) return 1;
    if (avg2 > avg1) return 2;
    return 0;
  };

  const radarData = comparison ? SKILLS.map(skill => ({
    skill: skill.label,
    [comparison.team1.team_name]: comparison.team1.averages?.[skill.key] || 0,
    [comparison.team2.team_name]: comparison.team2.averages?.[skill.key] || 0,
  })) : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/teams')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl md:text-2xl font-bold tracking-tight">
              Comparar Times
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {/* Team Selection */}
        <Card className="bg-card border-border/50 shadow-xl mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-primary" />
              Selecione os Times
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Time 1
                </Label>
                <Select value={team1Id} onValueChange={setTeam1Id}>
                  <SelectTrigger className="bg-input/50" data-testid="team1-select">
                    <SelectValue placeholder="Selecione o time 1" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsAverages.filter(t => t.total_players > 0).map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.team_name} ({team.total_players} jogadores)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-center">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-bold text-xl">VS</span>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Time 2
                </Label>
                <Select value={team2Id} onValueChange={setTeam2Id}>
                  <SelectTrigger className="bg-input/50" data-testid="team2-select">
                    <SelectValue placeholder="Selecione o time 2" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamsAverages.filter(t => t.total_players > 0).map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.team_name} ({team.total_players} jogadores)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                onClick={handleCompare}
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(0,122,255,0.3)] font-bold uppercase tracking-wider px-8"
                disabled={!team1Id || !team2Id}
                data-testid="compare-teams-btn"
              >
                <GitCompare className="mr-2 h-4 w-4" />
                Comparar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Comparison Result */}
        {comparison && (
          <div className="space-y-6">
            {/* Radar Chart */}
            <Card className="bg-card border-border/50 shadow-xl">
              <CardHeader>
                <CardTitle className="text-xl font-bold tracking-tight">
                  Gráfico Radar - Comparativo de Times
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#27272A" />
                      <PolarAngleAxis dataKey="skill" tick={RADAR_TICK} />
                      <PolarRadiusAxis angle={30} domain={RADAR_DOMAIN} tick={RADAR_RADIUS_TICK} />
                      <Radar
                        name={comparison.team1.team_name}
                        dataKey={comparison.team1.team_name}
                        stroke="#007AFF"
                        fill="#007AFF"
                        fillOpacity={0.3}
                      />
                      <Radar
                        name={comparison.team2.team_name}
                        dataKey={comparison.team2.team_name}
                        stroke="#CCFF00"
                        fill="#CCFF00"
                        fillOpacity={0.3}
                      />
                      <Legend />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Team Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Team 1 Card */}
              <Card className={`bg-card border-border/50 shadow-xl ${getWinner() === 1 ? 'ring-2 ring-yellow-400' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getWinner() === 1 && <Crown className="h-6 w-6 text-yellow-400" />}
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold">
                          {comparison.team1.team_name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">
                            <Users className="h-3 w-3 mr-1" />
                            {comparison.team1.total_players} jogadores
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className={`text-4xl font-black ${getScoreColor(parseFloat(getOverallAverage(comparison.team1)))}`}>
                      {getOverallAverage(comparison.team1)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {comparison.team1.total_evaluations} avaliações totais
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {SKILLS.map((skill) => (
                      <div key={skill.key} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                        <span className="text-sm">{skill.label}</span>
                        <span className={`font-bold ${getScoreColor(comparison.team1.averages?.[skill.key] || 0)}`}>
                          {comparison.team1.averages?.[skill.key] || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Team 2 Card */}
              <Card className={`bg-card border-border/50 shadow-xl ${getWinner() === 2 ? 'ring-2 ring-yellow-400' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getWinner() === 2 && <Crown className="h-6 w-6 text-yellow-400" />}
                      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold">
                          {comparison.team2.team_name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">
                            <Users className="h-3 w-3 mr-1" />
                            {comparison.team2.total_players} jogadores
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className={`text-4xl font-black ${getScoreColor(parseFloat(getOverallAverage(comparison.team2)))}`}>
                      {getOverallAverage(comparison.team2)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {comparison.team2.total_evaluations} avaliações totais
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {SKILLS.map((skill) => (
                      <div key={skill.key} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
                        <span className="text-sm">{skill.label}</span>
                        <span className={`font-bold ${getScoreColor(comparison.team2.averages?.[skill.key] || 0)}`}>
                          {comparison.team2.averages?.[skill.key] || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!comparison && !isLoading && teamsAverages.filter(t => t.total_players > 0).length < 2 && (
          <div className="text-center py-12 text-muted-foreground">
            <Trophy className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>Você precisa de pelo menos 2 times com jogadores para comparar</p>
            <Button
              variant="link"
              onClick={() => navigate('/teams')}
              className="text-primary mt-2"
            >
              Gerenciar Times
            </Button>
          </div>
        )}

        {!comparison && !isLoading && teamsAverages.filter(t => t.total_players > 0).length >= 2 && (
          <div className="text-center py-12 text-muted-foreground">
            <GitCompare className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>Selecione dois times acima para comparar o desempenho médio</p>
          </div>
        )}
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/teams')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Trophy className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/teams/compare')}
          className="flex flex-col items-center gap-1 h-auto py-2 text-primary"
        >
          <GitCompare className="h-5 w-5" />
          <span className="text-xs">Comparar</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
